import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type AuditLogEntry, type Game } from '../../services/api';

@customElement('audit-log-viewer')
export class AuditLogViewer extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String })
  gameId = '';

  @state()
  private game: Game | null = null;

  @state()
  private entries: AuditLogEntry[] = [];

  @state()
  private loading = true;

  @state()
  private loadingMore = false;

  @state()
  private hasMore = true;

  @state()
  private offset = 0;

  private readonly limit = 20;

  connectedCallback() {
    super.connectedCallback();
    // Note: gameId may not be set yet due to Lit property timing
  }

  firstUpdated() {
    // Handle initial load when gameId is set via property binding
    if (this.gameId) {
      this.loadData();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    // Handle subsequent gameId changes
    if (changedProps.has('gameId') && this.gameId && changedProps.get('gameId') !== undefined) {
      this.reset();
      this.loadData();
    }
  }

  private reset() {
    this.entries = [];
    this.offset = 0;
    this.hasMore = true;
  }

  private async loadData() {
    if (!this.gameId) return;

    this.loading = true;
    try {
      const [game, entries] = await Promise.all([
        api.getGame(this.gameId),
        api.getGameAuditLog(this.gameId, this.limit, 0),
      ]);
      this.game = game;
      this.entries = entries || [];
      this.hasMore = entries.length === this.limit;
      this.offset = entries.length;
    } catch (error) {
      console.error('Failed to load audit log:', error);
    } finally {
      this.loading = false;
    }
  }

  private async loadMore() {
    if (!this.gameId || this.loadingMore || !this.hasMore) return;

    this.loadingMore = true;
    try {
      const entries = await api.getGameAuditLog(this.gameId, this.limit, this.offset);
      this.entries = [...this.entries, ...(entries || [])];
      this.hasMore = entries.length === this.limit;
      this.offset += entries.length;
    } catch (error) {
      console.error('Failed to load more entries:', error);
    } finally {
      this.loadingMore = false;
    }
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private getChangeDescription(entry: AuditLogEntry): string {
    const diff = entry.new_value - entry.old_value;
    if (entry.old_value === 0 && entry.new_value === 0) {
      return 'Player added to game';
    }
    if (diff > 0) {
      return `Increased by ${diff}`;
    }
    if (diff < 0) {
      return `Decreased by ${Math.abs(diff)}`;
    }
    return 'No change';
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex justify-center py-8">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;
    }

    if (!this.game) {
      return html`
        <div class="alert alert-warning">
          <span>Game not found</span>
        </div>
      `;
    }

    return html`
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div>
            <h3 class="text-lg font-semibold">Audit Log</h3>
            <p class="text-sm text-base-content/70">
              ${this.game.name} • ${this.entries.length} ${this.entries.length === 1 ? 'entry' : 'entries'}
            </p>
          </div>
          <button
            class="btn btn-sm btn-ghost w-full sm:w-auto"
            @click=${() => { this.reset(); this.loadData(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        <!-- Audit entries -->
        ${this.entries.length > 0 ? html`
          <div class="space-y-3">
            ${this.entries.map(entry => this.renderEntry(entry))}
          </div>

          <!-- Load more button -->
          ${this.hasMore ? html`
            <div class="text-center pt-4">
              <button
                class="btn btn-ghost btn-sm"
                @click=${this.loadMore}
                ?disabled=${this.loadingMore}
              >
                ${this.loadingMore
                  ? html`<span class="loading loading-spinner loading-sm"></span>`
                  : 'Load More'}
              </button>
            </div>
          ` : nothing}
        ` : html`
          <div class="text-center py-8 text-base-content/70">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No audit entries yet.</p>
            <p class="text-sm mt-2">Changes to pitch counts will appear here.</p>
          </div>
        `}
      </div>
    `;
  }

  private renderEntry(entry: AuditLogEntry) {
    return html`
      <div class="card bg-base-200">
        <div class="card-body py-3 px-4">
          <div class="flex flex-wrap gap-x-4 gap-y-2 items-start justify-between">
            <!-- Left: Change info -->
            <div class="flex-1 min-w-48">
              <div class="flex items-center gap-2">
                <span class="font-semibold">${entry.player_name || 'Unknown Player'}</span>
                <span class="badge badge-sm ${this.getChangeBadgeClass(entry)}">
                  ${entry.old_value} → ${entry.new_value}
                </span>
              </div>
              <p class="text-sm text-base-content/70 mt-1">
                ${this.getChangeDescription(entry)}
              </p>
              ${entry.change_reason ? html`
                <p class="text-sm mt-1 italic">
                  "${entry.change_reason}"
                </p>
              ` : nothing}
            </div>

            <!-- Right: Meta info -->
            <div class="text-right text-sm text-base-content/70">
              <p>${entry.admin_name || 'Unknown Admin'}</p>
              <p>${this.formatDate(entry.created_at)}</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private getChangeBadgeClass(entry: AuditLogEntry): string {
    const diff = entry.new_value - entry.old_value;
    if (diff > 0) return 'badge-success';
    if (diff < 0) return 'badge-error';
    return 'badge-info';
  }
}
